/**
 * Stage 3 — plan vocabulary and how a plan is read off the real user object.
 *
 * This file contains NO mock data. It only interprets what the backend already
 * sends. The mock lives next door in mockAccess.js and is consulted solely when
 * the account has no real subscription.
 */

export const PLAN = {
  NONE: "none",
  SCHOOL: "school",
  TUTOR: "tutor",
  COACHING: "coaching",
};

export const PLAN_LABEL = {
  [PLAN.NONE]: "No plan",
  [PLAN.SCHOOL]: "School plan",
  [PLAN.TUTOR]: "Individual Tutor plan",
  [PLAN.COACHING]: "Coaching plan",
};

/**
 * Where each plan lands a *new* user after login.
 *
 * Existing paid accounts never come through here — see resolveLoginLandingPath
 * in useAccess.js, which sends them to /dashboard so MainDashboard can pick
 * their home component exactly as it does today.
 */
export const PLAN_HOME = {
  [PLAN.SCHOOL]: "/dashboard",
  [PLAN.TUTOR]: "/tutor",
  [PLAN.COACHING]: "/coaching",
};

/** The dashboard area a route belongs to. */
export const AREA = {
  SCHOOL: "school",
  TUTOR: "tutor",
  COACHING: "coaching",
};

/** Existing route for plans. Nothing here processes a payment. */
export const PRICING_ROUTE = "/pricing";

/**
 * Roles that own their own subscription. Every other role is a sub-account
 * under a parent and inherits that parent's access untouched.
 * Kept in sync with Stage 1.
 */
export const PRIMARY_ACCOUNT_ROLES = ["super admin", "trial", "guest_user"];

export const isPrimaryAccount = (user) =>
  PRIMARY_ACCOUNT_ROLES.includes(user?.roles?.toLowerCase());

/** Real, backend-issued subscription. Same check the app already uses. */
export const hasBackendSubscription = (user) =>
  Array.isArray(user?.payments) && user.payments.length > 0;

/** Backend-issued trial window, already consumed by Home.js, GuestUserHome.js et al. */
export const hasBackendTrial = (user) => Boolean(user?.trial_period_end_time);

/**
 * Which product a real paying account is on.
 *
 * `plan_type` on a payment is the school tier (complete / customized /
 * special), not the product, so the product comes from account_type — the
 * same field registration writes and appdAiHelpers.getUserType() reads.
 */
export const planFromAccountType = (user) => {
  switch ((user?.account_type || "").toLowerCase()) {
    case "individual":
      return PLAN.TUTOR;
    case "coaching":
      return PLAN.COACHING;
    case "school":
      return PLAN.SCHOOL;
    default:
      return PLAN.SCHOOL;
  }
};

/** Stable per-user key so a trial never leaks between accounts on one device. */
export const userKey = (user) => String(user?.admin_id ?? user?.id ?? "anon");
